import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import emailService from '../../services/emailService';
import { fetchEmployees, type EmployeeListItem } from '../../api/employees';

export default function EmailSend() {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Données
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Formulaire
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const emps = await fetchEmployees();
      // Filtrer les employés avec email valide
      const filtered = emps.filter((e) => {
        const hasValidEmail = e.email && e.email.trim() !== '' && e.email !== 'NON RENSEIGNÉ';
        return hasValidEmail;
      });
      setEmployees(filtered);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (empId: string) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachmentFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    // Validation
    if (selectedEmployees.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    if (!subject.trim()) {
      toast.error('Veuillez saisir un objet');
      return;
    }
    if (!message.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }

    setSending(true);
    try {
      // Créer la campagne
      const campaign = await emailService.create({
        subject,
        message,
        send_to_all: false,
        departments: [],
        employees: selectedEmployees,
        attachment_files: attachmentFiles,
      });

      if (!campaign || !campaign.id) {
        throw new Error('La campagne n\'a pas été créée correctement');
      }

      // Envoyer immédiatement
      const result = await emailService.send(campaign.id);

      toast.success(`✓ Email envoyé à ${result.sent_count} destinataire(s)`);

      // Réinitialiser le formulaire
      setSelectedEmployees([]);
      setSubject('');
      setMessage('');
      setAttachmentFiles([]);
    } catch (error: any) {
      console.error('Erreur envoi:', error);
      
      const errorData = error.response?.data;
      
      if (errorData?.error) {
        let errorMessage = errorData.error;
        
        if (errorData.details) {
          errorMessage += `\n\n${errorData.details}`;
        }
        
        if (errorData.help) {
          errorMessage += `\n\n💡 ${errorData.help}`;
        }
        
        toast.error(errorMessage, { duration: 8000 });
      } else {
        toast.error(error.response?.data?.error || error.message || 'Erreur lors de l\'envoi');
      }
    } finally {
      setSending(false);
    }
  };

  // Filtrage des employés
  const filteredEmployees = employees.filter((emp) =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="card bg-white shadow-xl">
      <div className="card-body space-y-6">
        {/* 1. DESTINATAIRES */}
        <div>
          <label className="label">
            <span className="label-text font-semibold text-lg">Destinataires</span>
            <span className="label-text-alt">{selectedEmployees.length} sélectionné(s)</span>
          </label>

          {/* Recherche */}
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input input-bordered w-full mb-3"
          />

          {/* Liste des employés */}
          <div className="border-2 border-blue-200 rounded-lg max-h-64 overflow-y-auto bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <span className="loading loading-spinner text-blue-500"></span>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {employees.length === 0
                  ? 'Aucun employé avec email trouvé'
                  : 'Aucun résultat'}
              </div>
            ) : (
              <table className="table table-sm">
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr
                      key={emp.id}
                      className={`cursor-pointer hover:bg-blue-50 ${
                        selectedEmployees.includes(String(emp.id)) ? 'bg-blue-100' : ''
                      }`}
                      onClick={() => toggleEmployee(String(emp.id))}
                    >
                      <td className="w-12">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm border-blue-400 checked:border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:white]"
                          checked={selectedEmployees.includes(String(emp.id))}
                          onChange={() => toggleEmployee(String(emp.id))}
                        />
                      </td>
                      <td className="font-medium text-black">{emp.full_name}</td>
                      <td className="text-sm text-gray-600">{emp.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 2. OBJET */}
        <div>
          <label className="label">
            <span className="label-text font-semibold text-lg">Objet</span>
          </label>
          <input
            type="text"
            placeholder="Objet de l'email"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input input-bordered w-full"
          />
        </div>

        {/* 3. PIÈCES JOINTES */}
        <div>
          <label className="label">
            <span className="label-text font-semibold text-lg flex items-center gap-2 text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Pièces jointes
            </span>
          </label>

          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="file-input file-input-bordered border-blue-300 w-full focus:border-blue-500"
          />

          {attachmentFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachmentFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 p-2 rounded">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="flex-1 text-black">{file.name}</span>
                  <span className="text-xs text-gray-600">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="btn btn-ghost btn-xs hover:bg-red-100 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 4. MESSAGE */}
        <div>
          <label className="label">
            <span className="label-text font-semibold text-lg">Message</span>
          </label>
          <textarea
            placeholder="Votre message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="textarea textarea-bordered w-full h-48"
          />
        </div>

        {/* 5. BOUTON ENVOYER */}
        <div className="card-actions justify-end pt-4">
          <button
            onClick={handleSend}
            disabled={sending}
            className="btn btn-lg w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold border-none shadow-lg"
          >
            {sending ? (
              <>
                <span className="loading loading-spinner"></span>
                Envoi en cours...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Envoyer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
